import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { fetchCheckoutStatus } from "../lib/api";
import { Button } from "../components/ui/button";
import Seo from "../components/Seo";
import { Check, AlertCircle, Loader2 } from "lucide-react";

export default function BookSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState({ state: "checking", attempts: 0 });

  useEffect(() => {
    if (!sessionId) { setStatus({ state: "error", message: "Missing session id" }); return; }
    let alive = true;
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const data = await fetchCheckoutStatus(sessionId);
        if (!alive) return;
        if (data.payment_status === "paid") {
          setStatus({ state: "paid", data });
          return;
        }
        if (data.status === "expired") {
          setStatus({ state: "expired", data });
          return;
        }
        if (attempts < 8) {
          setStatus({ state: "polling", attempts });
          setTimeout(poll, 2500);
        } else {
          setStatus({ state: "pending", data });
        }
      } catch (e) {
        if (alive) setStatus({ state: "error", message: "Could not verify payment" });
      }
    };
    poll();
    return () => { alive = false; };
  }, [sessionId]);

  return (
    <>
      <Seo title="Booking confirmation — Lime Driving School" />
      <section className="py-20 md:py-32 max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-testid="booking-success-page">
        {status.state === "paid" && (
          <>
            <div className="mx-auto h-16 w-16 rounded-full bg-[#37B160] grid place-items-center">
              <Check className="h-8 w-8 text-white" />
            </div>
            <h1 className="mt-6 text-3xl sm:text-4xl tracking-tight font-medium" style={{fontFamily:"Outfit, sans-serif"}} data-testid="success-heading">
              You're booked in!
            </h1>
            <p className="mt-3 text-[#4B5563]">
              We've emailed your full schedule to your inbox. Akram will be in touch before your first lesson.
            </p>
            <div className="mt-4 text-sm text-[#9CA3AF]" data-testid="order-id">Order ID: {status.data?.order_id || "—"}</div>
            <div className="mt-8">
              <Link to="/"><Button className="bg-[#37B160] hover:bg-[#2D9450] text-white">Back to home</Button></Link>
            </div>
          </>
        )}
        {status.state === "polling" || status.state === "checking" ? (
          <>
            <Loader2 className="h-10 w-10 animate-spin mx-auto text-[#37B160]" />
            <h1 className="mt-6 text-2xl font-medium" style={{fontFamily:"Outfit, sans-serif"}}>Confirming your payment…</h1>
            <p className="mt-2 text-sm text-[#9CA3AF]">This usually takes 5–10 seconds.</p>
          </>
        ) : null}
        {status.state === "pending" && (
          <>
            <AlertCircle className="h-10 w-10 mx-auto text-amber-500" />
            <h1 className="mt-4 text-2xl font-medium" style={{fontFamily:"Outfit, sans-serif"}}>Payment processing</h1>
            <p className="mt-2 text-[#4B5563]">We're still verifying your payment. You'll get an email as soon as it's confirmed.</p>
          </>
        )}
        {status.state === "expired" && (
          <>
            <AlertCircle className="h-10 w-10 mx-auto text-red-500" />
            <h1 className="mt-4 text-2xl font-medium" style={{fontFamily:"Outfit, sans-serif"}}>Session expired</h1>
            <p className="mt-2 text-[#4B5563]">Your booking session timed out. Please try again.</p>
            <Link to="/book"><Button className="mt-6 bg-[#37B160] hover:bg-[#2D9450] text-white">Book again</Button></Link>
          </>
        )}
        {status.state === "error" && (
          <>
            <AlertCircle className="h-10 w-10 mx-auto text-red-500" />
            <h1 className="mt-4 text-2xl font-medium" style={{fontFamily:"Outfit, sans-serif"}}>Something went wrong</h1>
            <p className="mt-2 text-[#4B5563]">{status.message}</p>
          </>
        )}
      </section>
    </>
  );
}
